import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { verificationAPI } from '../services/api';

const str = (v) => (v === undefined || v === null ? '' : String(v).toUpperCase());
const isTrueish = (v) =>
  v === true || v === 1 || str(v) === 'TRUE' || str(v) === 'VERIFIED' || str(v) === 'APPROVED';

const getFulfilledData = (result) => (result.status === 'fulfilled' ? result.value?.data : null);
const normalizePayload = (payload) => payload?.data ?? payload ?? {};

const getProfileContact = (profile, user) => ({
  email: profile?.email || profile?.userEmail || user?.email || '',
  phone: profile?.phone || profile?.phoneNumber || '',
});

const setAccountVerificationStep = (step) => {
  try {
    sessionStorage.setItem('av_verificationStep', step);
  } catch {
    // ignore
  }
};

const buildVerifyUrl = (step, email, phone) => {
  const params = new URLSearchParams({ step, email });
  if (phone) params.set('phone', phone);
  return `/verify?${params.toString()}`;
};

function deriveVerificationState(status, profile) {
  const emailVerified =
    status?.emailVerified ??
    status?.isEmailVerified ??
    status?.email?.verified ??
    isTrueish(profile?.emailVerified) ??
    false;

  const phoneVerified =
    status?.phoneVerified ??
    status?.isPhoneVerified ??
    status?.phone?.verified ??
    isTrueish(profile?.phoneVerified) ??
    false;

  const docsVerifiedApi = isTrueish(
    status?.isDocumentVerified ??
      status?.documentsVerified ??
      status?.isDocumentsVerified ??
      status?.documents?.verified ??
      status?.documentVerificationStatus ??
      profile?.documentsVerified
  );

  const fullyVerified =
    isTrueish(status?.fullyVerified ?? status?.verificationComplete ?? status?.isFullyVerified) ||
    (Boolean(emailVerified) && Boolean(phoneVerified) && docsVerifiedApi);

  const canAccessDashboard = isTrueish(status?.canAccessDashboard);

  const statusCode =
    status?.statusCode ??
    status?.verificationStatus?.statusCode ??
    profile?.verificationStatus?.statusCode ??
    profile?.statusCode ??
    null;

  const docUploadStage =
    statusCode === 4 ||
    str(status?.status) === 'DOCUMENT_UPLOAD' ||
    str(profile?.verificationStatus?.status) === 'DOCUMENT_UPLOAD';

  const hasDocUploadedKey = Object.hasOwn(status, 'isDocumentUploaded');
  const hasDocVerifiedKey = Object.hasOwn(status, 'isDocumentVerified');
  const needsDocumentUpload =
    hasDocUploadedKey ? !isTrueish(status.isDocumentUploaded) : docUploadStage || !docsVerifiedApi;
  const pendingDocumentReview =
    (hasDocUploadedKey && isTrueish(status.isDocumentUploaded) && !docsVerifiedApi) ||
    (hasDocVerifiedKey && !isTrueish(status.isDocumentVerified) && !needsDocumentUpload);

  return {
    emailVerified,
    phoneVerified,
    fullyVerified,
    canAccessDashboard,
    needsDocumentUpload,
    pendingDocumentReview,
  };
}

function computeRedirectPath(verificationState, profile, user) {
  const {
    emailVerified,
    phoneVerified,
    fullyVerified,
    canAccessDashboard,
    needsDocumentUpload,
    pendingDocumentReview,
  } = verificationState;

  if (fullyVerified || canAccessDashboard) return null;

  const { email, phone } = getProfileContact(profile, user);

  if (!emailVerified) return buildVerifyUrl('email', email, phone);
  if (!phoneVerified) return buildVerifyUrl('phone', email, phone);

  if (pendingDocumentReview) {
    if (email) return buildVerifyUrl('pending', email);
    setAccountVerificationStep('inProgress');
    return '/account-verification';
  }

  if (needsDocumentUpload) {
    setAccountVerificationStep('form');
    return '/account-verification';
  }

  if (email) return buildVerifyUrl('pending', email);

  setAccountVerificationStep('form');
  return '/account-verification';
}

/**
 * A wrapper component that protects routes from unauthenticated access.
 * If the user is not logged in, they are redirected to the login page.
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const [redirectTo, setRedirectTo] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setStatusLoading(false);
      setRedirectTo(null);
      return undefined;
    }

    let cancelled = false;
    const run = async () => {
      setStatusLoading(true);
      try {
        const [statusResp, profileResp] = await Promise.allSettled([
          verificationAPI.getStatus(),
          verificationAPI.getProfile(),
        ]);

        const status = normalizePayload(getFulfilledData(statusResp));
        const profile = normalizePayload(getFulfilledData(profileResp));
        const verificationState = deriveVerificationState(status, profile);
        const path = computeRedirectPath(verificationState, profile, user);

        if (!cancelled) setRedirectTo(path);
      } catch {
        // If status APIs fail, don't block the user.
        if (!cancelled) setRedirectTo(null);
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  if (!isAuthenticated) {
    // Redirect unauthenticated users to the login page.
    // The `state` prop lets us redirect back after successful login if desired.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (statusLoading) return null;
  return redirectTo ? <Navigate to={redirectTo} replace /> : children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ProtectedRoute;
